import React, { useState, useEffect, useRef } from "react";
import { Box, Tabs, Tab, Typography, Container, useMediaQuery, useTheme } from "@mui/material";
import { motion } from "framer-motion";
import { gsap } from "gsap";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";
import { backsidebackgrounddesktop, backsidebackgroundmobile, gardientline } from "../../../assets";

type FloorPlans = {
  [key: string]: {
    [key: string]: string[];
  };
};

const floorPlans: FloorPlans = {
  east: {
    "500": [
      "https://dprstorage.b-cdn.net/kolla/Luxuria/Floorplans/LFP%20-07.png",
      "https://dprstorage.b-cdn.net/kolla/Luxuria/Floorplans/LFP%20-09.png",
      "https://dprstorage.b-cdn.net/kolla/Luxuria/Floorplans/LFP%20-08.png",
    ],
  },
  west: {
    "350": [
      "https://dprstorage.b-cdn.net/kolla/Luxuria/Floorplans/LFP%20W350-10.png",
      "https://dprstorage.b-cdn.net/kolla/Luxuria/Floorplans/LFP%20W350-11.png",
      "https://dprstorage.b-cdn.net/kolla/Luxuria/Floorplans/LFP%20W350-12.png",
    ],
    "500": [
      "https://dprstorage.b-cdn.net/kolla/Luxuria/Floorplans/LFP%20W500-13.png",
      "https://dprstorage.b-cdn.net/kolla/Luxuria/Floorplans/LFP%20W500-14.png",
      "https://dprstorage.b-cdn.net/kolla/Luxuria/Floorplans/LFP%20W500-15.png",
    ],
  },
};

const FloorPlansNew = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [selectedSection, setSelectedSection] = useState<keyof typeof floorPlans>("east");
  const [selectedPlan, setSelectedPlan] = useState<string>("500");
  const [selectedImage, setSelectedImage] = useState<string>(floorPlans["east"]["500"][0]);
  const [isOpen, setIsOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const imageRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    if (imageRef.current) {
      gsap.fromTo(
        imageRef.current,
        { opacity: 0, scale: 0.8 },
        { opacity: 1, scale: 1, duration: 0.8, ease: "power2.out" }
      );
    }
  }, [selectedImage]);

  const handleTabChange = (newSection: keyof typeof floorPlans, newPlan: string) => {
    const newImage = floorPlans[newSection][newPlan][0];
    if (imageRef.current) {
      gsap.to(imageRef.current, {
        opacity: 0,
        scale: 0.8,
        duration: 0.5,
        ease: "power2.in",
        onComplete: () => {
          setSelectedSection(newSection);
          setSelectedPlan(newPlan);
          setSelectedImage(newImage);
        },
      });
    } else {
      setSelectedSection(newSection);
      setSelectedPlan(newPlan);
      setSelectedImage(newImage);
    }
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: isMobile ? "auto" : "100vh",
        backgroundImage: isMobile ? `url(${backsidebackgroundmobile})` : `url(${backsidebackgrounddesktop})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        px: { xs: 2, md: 1 },
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          color: "white",
          height: isMobile ? "auto" : "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            height: "20vh",
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontWeight: 600,
              fontSize: { xs: 24, md: 28 },
              background: "linear-gradient(90deg, #bf742c, #fff8da, #c58038)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Floor Plans
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
            <img src={gardientline} alt="Best apartments in Hyderabad by Kolla Group" style={{ width: "100%", maxWidth: "700px" }} />
          </Box>
        </Container>

        {/* Tabs */}
        <Box sx={{ width: "100%", mb: 2 }}>
          <Tabs
            value={selectedSection}
            onChange={(event, newValue) => handleTabChange(newValue, Object.keys(floorPlans[newValue])[0])}
            textColor="inherit"
            indicatorColor="primary"
            centered
            sx={{ mb: 1 }}
          >
            {Object.keys(floorPlans).map((section) => (
              <Tab
                key={section}
                label={section.toUpperCase()}
                value={section}
                sx={{
                  textTransform: "uppercase",
                  fontWeight: selectedSection === section ? "bold" : "normal",
                  borderBottom: selectedSection === section ? "2px solid #c58038" : "none",
                  background: selectedSection === section ? "linear-gradient(90deg, #bf742c, #fff8da, #c58038)" : "none",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: selectedSection === section ? "transparent" : "inherit",
                }}
              />
            ))}
          </Tabs>

          <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
            <Tabs
              value={selectedPlan}
              onChange={(event, newValue) => handleTabChange(selectedSection, newValue)}
              textColor="inherit"
              indicatorColor="primary"
              centered
              sx={{
                "& .MuiTabs-flexContainer": {
                  gap: "0px", // To remove the gap between tabs
                },
              }}
            >
              {Object.keys(floorPlans[selectedSection]).map((plan) => (
                <Tab
                  key={plan}
                  label={
                    <Box>
                      <Typography
                        variant="h5"
                        sx={{
                          fontSize: selectedPlan === plan ? 12 : 12, // Reduced font size
                          fontWeight: "600",
                        }}
                      >
                        {plan + " SQ. Yds"}
                      </Typography>
                    </Box>
                  }
                  value={plan}
                  sx={{
                    background: selectedPlan === plan
                      ? "linear-gradient(90deg, #bf742c, #fff8da, #c58038)"
                      : "black",
                    color: selectedPlan === plan ? "black" : "white",
                    fontWeight: "600",
                    px: 3, // Reduced padding
                    py: 0.05, // Reduced padding
                    border: "2px solid rgb(146, 146, 146)",
                    borderRadius: 0,
                    "&:hover": {
                      background: "linear-gradient(90deg, #bf742c, #fff8da, #c58038)",
                      color: "black",
                    },
                    transition: "all 0.3s ease-in-out",
                  }}
                />
              ))}
            </Tabs>
          </Box>
        </Box>

        {/* Content */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            justifyContent: "center",
            // height: "100%",
            gap: 3,
            width: "100%",
          }}
        >
          {/* Main Image */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: { xs: "auto", md: "100%" },
              mr: { xs: 0, md: "250px" },
              width: { xs: "100%", md: "auto" },
              mt: { xs: 1, md: -5 },
            }}
          >
            <img
              ref={imageRef}
              src={selectedImage}
              alt="Best apartments in Hyderabad by Kolla Group"
              className="main-image"
              style={{
                width: "auto",
                height: isMobile ? "530px" : "600px",
                borderRadius: "10px",
                boxShadow: "0px 5px 15px rgba(0,0,0,0.3)",
                objectFit: "contain",
                transform: `rotate(${isMobile ? 0 : 90}deg)`,
                opacity: 0,
                transformOrigin: "center",
                transition: "opacity 0.5s ease, transform 0.5s ease",
                cursor: "pointer",
              }}
              onClick={() => {
                setPhotoIndex(floorPlans[selectedSection][selectedPlan].indexOf(selectedImage));
                setIsOpen(true);
              }}
            />
          </Box>

          {/* Thumbnails */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "row", md: "column" },
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
            }}
          >
            {floorPlans[selectedSection][selectedPlan].map((img, index) => (
              <motion.img
                key={index}
                src={img}
                alt={`Thumbnail ${index + 1}`}
                className="thumbnail-image"
                style={{
                  width: "auto",
                  height: "80px",
                  cursor: "pointer",
                  borderRadius: "8px",
                  border: selectedImage === img ? "3px solid #c58038" : "2px solid transparent",
                  boxShadow: "0px 2px 10px rgba(0,0,0,0.2)",
                  objectFit: "cover",
                }}
                whileHover={{ scale: 1.1 }}
                onClick={() => setSelectedImage(img)}
              />
            ))}
          </Box>
        </Box>
      </Box>

      {/* Lightbox Modal */}
      {isOpen && (
        <Lightbox
          mainSrc={floorPlans[selectedSection][selectedPlan][photoIndex]}
          nextSrc={
            floorPlans[selectedSection][selectedPlan][
              (photoIndex + 1) % floorPlans[selectedSection][selectedPlan].length
            ]
          }
          prevSrc={
            floorPlans[selectedSection][selectedPlan][
              (photoIndex + floorPlans[selectedSection][selectedPlan].length - 1) %
                floorPlans[selectedSection][selectedPlan].length
            ]
          }
          onCloseRequest={() => setIsOpen(false)}
          onMovePrevRequest={() =>
            setPhotoIndex(
              (photoIndex + floorPlans[selectedSection][selectedPlan].length - 1) %
                floorPlans[selectedSection][selectedPlan].length
            )
          }
          onMoveNextRequest={() =>
            setPhotoIndex((photoIndex + 1) % floorPlans[selectedSection][selectedPlan].length)
          }
        />
      )}
    </Box>
  );
};

export default FloorPlansNew;